import mixpanel from "mixpanel-browser";
import React, { PropsWithChildren, useContext, useMemo, useState } from "react";
import { useFlag } from "react-unleash-flags";
import { v4 as uuid } from "uuid";

import Toaster, { ToasterMarketingPromptProps } from ".";
import useSelf from "../../../hooks/useSelf";
import { ToastContent, ToastOptions } from "./types";

const TOAST_REMOVAL_DELAY = 350;
const SUCCESS_TOAST_TIMEOUT = 8000;

enum RemoveReason {
  MANUAL,
  OVERFLOW,
  TIMEOUT,
}

export interface ToastData {
  closed?: boolean;
  id: string;
  content: ToastContent;
  options: ToastOptions;
}

type VariantOptions = Omit<ToastOptions, "variant">;

export interface ToastMethods {
  show(content: ToastContent, options?: ToastOptions): string;
  remove(id: string): void;
  success(content: ToastContent, options?: VariantOptions): string;
  danger(content: ToastContent, options?: VariantOptions): string;
  info(content: ToastContent, options?: VariantOptions): string;
  warning(content: ToastContent, options?: VariantOptions): string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(error: any, options?: VariantOptions): string;
}

const context = React.createContext<ToastMethods | null>(null);

export function useToast(): ToastMethods {
  const payload = useContext(context)!;
  if (payload) return payload;

  if (process.env.NODE_ENV === "test") {
    let id = 0;
    return {
      show: () => (id++).toString(),
      remove: () => null,
      success: () => (id++).toString(),
      danger: () => (id++).toString(),
      info: () => (id++).toString(),
      warning: () => (id++).toString(),
      error: () => (id++).toString(),
    };
  }

  throw new Error("Do not call useToast outside the ToastProvider");
}

export function ToastProvider({
  children,
  showNewVersionToast,
  showExtraMarketingPrompt,
  extraMarketingPromptFeatureId,
  extraMarketingPromptBody,
  extraMarketingPromptCta,
  extraMarketingPromptLink,
}: PropsWithChildren<
  {
    showNewVersionToast: boolean;
  } & ToasterMarketingPromptProps
>) {
  const versionMismatchFeatureFlag = useFlag("version_mismatch_notice");
  const self = useSelf();

  const [toasts, setToasts] = useState<ToastData[]>([]);

  const methods = useMemo<ToastMethods>(() => {
    function hardRemove(id: string) {
      setToasts((toasts) => {
        if (!toasts.some((toast) => toast.id === id)) return toasts;
        return toasts.filter((toast) => toast.id !== id);
      });
    }

    function remove(id: string, reason: RemoveReason) {
      setToasts((toasts) => {
        const toast = toasts.find((toast) => toast.id === id);
        if (!toast || toast.closed) return toasts;
        if (reason !== RemoveReason.TIMEOUT) {
          mixpanel.track("Dismissed a toast", {
            reason: reason === RemoveReason.MANUAL ? "user" : "overflow",
            variant: toast.options.variant,
          });
        }
        setTimeout(() => hardRemove(id), TOAST_REMOVAL_DELAY);
        return toasts.map((toast) => (toast.id === id ? { closed: true, ...toast } : toast));
      });
    }

    function show(content: ToastContent, options: ToastOptions = {}) {
      const id = uuid();
      setToasts((toasts) => {
        if (options.variant === "success" && !options.persistent) {
          setTimeout(() => remove(id, RemoveReason.TIMEOUT), SUCCESS_TOAST_TIMEOUT);
        }
        for (let i = 3; i < toasts.length; ++i) {
          const {
            id,
            options: { persistent },
          } = toasts[i];
          if (!persistent) {
            setImmediate(() => remove(id, RemoveReason.OVERFLOW));
          }
        }
        return [{ id, content, options }, ...toasts];
      });
      return id;
    }

    return {
      show,
      remove: (id: string) => remove(id, RemoveReason.MANUAL),
      success(content, options) {
        return show(content, { ...options, variant: "success" });
      },
      danger(content, options) {
        return show(content, { ...options, variant: "danger" });
      },
      info(content, options) {
        return show(content, { ...options, variant: "info" });
      },
      warning(content, options) {
        return show(content, { ...options, variant: "warning" });
      },
      // Default helper for toasting thrown errors,
      // because try/catch makes your error object an unknown
      // and handling that here is better than handling it in every try/catch block.
      error(error, options) {
        return show(error?.message ?? error?.toString() ?? "An unknown error occurred.", {
          ...options,
          variant: "danger",
        });
      },
    };
  }, []);

  // This state is never updated, it just stores what the value was when the app started:
  const [shownAnalyticsPrompt] = useState(self.acceptTracking === "ASK");

  return (
    <context.Provider value={methods}>
      {children}
      {/* @ts-ignore -- the types of the extra marketing prompt are a little bit too complex to be figured out */}
      <Toaster
        toasts={toasts}
        methods={methods}
        showNewVersionToast={showNewVersionToast && !!versionMismatchFeatureFlag}
        showAnalyticsOptIn={self.acceptTracking === "ASK"}
        showNewsletterCta={!shownAnalyticsPrompt && !self.answeredNewsletterCta}
        showExtraMarketingPrompt={showExtraMarketingPrompt}
        extraMarketingPromptFeatureId={extraMarketingPromptFeatureId}
        extraMarketingPromptBody={extraMarketingPromptBody}
        extraMarketingPromptCta={extraMarketingPromptCta}
        extraMarketingPromptLink={extraMarketingPromptLink}
      />
    </context.Provider>
  );
}
